import { useEffect, useRef, useState } from "react";
import { Button, Field, Groupper, Header, Input, Message, Modal, Toast, Table, Toolbar } from "react-frontier";
import type { BlockType, DocumentBlock } from "@/Classes";
import { createDocumentBlock, getBlockTypes, getDocumentBlocks, updateDocumentBlock, deleteDocumentBlock, reorderDocumentBlocks } from "@/Api";
import { useSearchParams } from "react-router-dom";
import MDEditor, { commands } from '@uiw/react-md-editor';
import SortableList, { SortableItem } from "react-easy-sort";
import { arrayMoveImmutable } from "array-move";
import Validator from "@/Validator";
import UploadBox from "./UploadBox";

enum MODALS{
   LIST='block_list',
   TEXT='block_text',
   LIST_ENUM='block_list_enum',
   IMAGE='block_image',
   CODE='block_code',
   ACCORDION='block_accordion',
   STEPS='block_steps',
   TYPE_TABLE='block_type',
   WARNING='block_warning',
   QUOTE='block_quote',
   INFO='block_info',
   DIVIDER='block_divider',
   DELETE_ACTION='block_delete_action'
}

const INITVALUE:DocumentBlock={
   block_id: 0,
   document_id: 0,
   block_type: 0,
   content: {
      title: '',
      body: ''
   },
   priority: 0
}

var DocumentEdit=()=>{
   var[searchParams]=useSearchParams();
   var containerRef=useRef<HTMLDivElement>(null);
   var[width,setWidth]=useState<number>(0);
   var[error,setError]=useState<string>(null);
   var[modal,setModal]=useState<string>(null);
   var[blockType,setBlockType]=useState<BlockType>(null);
   var[blockTypes,setBlockTypes]=useState<BlockType[]>(null);
   var[modalPrompts,setModalPrompts]=useState<string[]>(null);
   var[documentBlocks,setDocumentBlocks]=useState<DocumentBlock[]>(null);
   var[documentBlock,setDocumentBlock]=useState<DocumentBlock>(INITVALUE);
   var[viewMode,setViewMode]=useState<number>(0);

   var document_id = searchParams.get('document_id') ? Number(searchParams.get('document_id')) : null;

   var onOpenModal=(blockType: BlockType, existingBlock?: DocumentBlock)=>{
      setModalPrompts(null);
      setBlockType(blockType);
      setModal(blockType.slug_name);

      if(existingBlock){
         setDocumentBlock(existingBlock);
         return;
      }

      let initialBody='';
      if(blockType.slug_name===MODALS.LIST)initialBody='- ';
      else if(blockType.slug_name===MODALS.LIST_ENUM)initialBody='1. ';
      else if(blockType.slug_name===MODALS.CODE)initialBody='```\n\n```';
      else if(blockType.slug_name === MODALS.QUOTE)initialBody='> ';
      else if(blockType.slug_name===MODALS.DIVIDER)initialBody='---';

      if(blockType.slug_name===MODALS.STEPS){
         setDocumentBlock({
            ...INITVALUE,
            content:{
               title: '',
               body: '',
               bodies: ['']
            }
         });
      }else if(blockType.slug_name===MODALS.ACCORDION){
         setDocumentBlock({
            ...INITVALUE,
            content:{
               title: '',
               body: '',
               items: [{title: '', body: ''}]
            }
         });
      }else{
         setDocumentBlock({
            ...INITVALUE,
            content:{
               ...INITVALUE.content,
               body: initialBody,
               file: {img: '', type: ''}
            }
         });
      }
   };

   var onCloseModal=()=>{
      setModal(null);
   };

   var onCreateDirectBlock=(bt: BlockType)=>{
      var contentObj={body:'---'};
      var content=JSON.stringify(contentObj);
      var maxOrder=documentBlocks?Math.max(...documentBlocks.map((db)=>db.priority),0):0;   

      createDocumentBlock(document_id, bt.block_type_id, content, maxOrder+1).then(res=>{

         setDocumentBlocks(prev=>[...prev||[],{
               block_id: res.data.block_id,
               block_type: bt.block_type_id,
               content: contentObj,
               document_id: document_id,
               priority: maxOrder+1
         }])

         Toast.success('Bloque insertado correctamente.');
      }).catch(()=>{
         Toast.error('Ocurrio un error al insertar el bloque. (DCB-CRT-2)');
      });
   };

   var onCreateDocumentBlock=(setLoading: (loading: boolean) => void)=>{
      setLoading(true);

      var validationResult: {valid: boolean, prompts: string[]};

      if(blockType?.slug_name===MODALS.STEPS){
         var bodies=documentBlock.content.bodies||[];
         if(bodies.length < 1 || bodies.every(b => !b.trim())){
            setLoading(false);
            return setModalPrompts(['Agrega al menos un paso con contenido.']);
         }
      }else if(blockType?.slug_name===MODALS.ACCORDION){
         var items = documentBlock.content.items || [];
         if(items.length < 1 || items.every(i => !i.title.trim() && !i.body.trim())){
            setLoading(false);
            return setModalPrompts(['Agrega al menos un elemento con titulo y contenido.']);
         }
         var emptyItem = items.find(i => !i.title.trim() || !i.body.trim());
         if(emptyItem){
            setLoading(false);
            return setModalPrompts(['Cada elemento debe tener titulo y contenido.']);
         }
      }else if(blockType.slug_name===MODALS.IMAGE){
         validationResult=Validator({
            img: documentBlock.content.file.img,
         },{
            img: [{ rule: 'empty', label: 'Imagen' }],
         });
         if(!validationResult.valid){
            setLoading(false);
            return setModalPrompts(validationResult.prompts);
         }
      }else{
         validationResult=Validator({
            body: documentBlock.content.body,
         },{
            body: [{ rule: 'empty', label: 'Contenido' }],
         });
         if(!validationResult.valid){
            setLoading(false);
            return setModalPrompts(validationResult.prompts);
         }
      }

      var content=JSON.stringify(documentBlock.content);
      var maxOrder=documentBlocks?Math.max(...documentBlocks.map((db)=>db.priority),0):0;   

      createDocumentBlock(document_id, blockType.block_type_id, content, maxOrder+1).then(res=>{
         if(res.error)return setModalPrompts([res.message]);

         setDocumentBlocks(prev=>[...prev||[],{
               block_id: res.data.block_id,
               block_type: blockType.block_type_id,
               content: documentBlock.content,
               document_id: document_id,
               priority: maxOrder+1
         }])

         Toast.success('Bloque creado correctamente.');

         setModal(null);
      }).catch(()=>{
         return setModalPrompts(['Ocurrio un error al crear el bloque. (DCB-CRT-1)']);
      }).finally(()=>{
         setLoading(false)
      });
   };

   var onUpdateDocumentBlock=(setLoading: (loading: boolean) => void)=>{
      setLoading(true);
      setModalPrompts(null);

      var validationResult: {valid: boolean, prompts: string[]};

      if(blockType?.slug_name === MODALS.STEPS){
         var bodies = documentBlock.content.bodies || [];
         if(bodies.length < 1 || bodies.every(b => !b.trim())){
            setLoading(false);
            return setModalPrompts(['Agrega al menos un paso con contenido.']);
         }
      }else if(blockType?.slug_name === MODALS.ACCORDION){
         var items = documentBlock.content.items || [];
         if(items.length < 1 || items.every(i => !i.title.trim() && !i.body.trim())){
            setLoading(false);
            return setModalPrompts(['Agrega al menos un elemento con titulo y contenido.']);
         }
         var emptyItem = items.find(i => !i.title.trim() || !i.body.trim());
         if(emptyItem){
            setLoading(false);
            return setModalPrompts(['Cada elemento debe tener titulo y contenido.']);
         }
      }else{
         validationResult=Validator({
            title: documentBlock.content.title,
            body: documentBlock.content.body,
         },{
            body: [{ rule: 'empty', label: 'Contenido' }],
         });
         if(!validationResult.valid){
            setLoading(false);
            return setModalPrompts(validationResult.prompts);
         }
      }

      var content=JSON.stringify(documentBlock.content);

      updateDocumentBlock(documentBlock.block_id, content).then(res=>{
         if(res.error)return setModalPrompts([res.message]);

         setDocumentBlocks(prev=>prev.map(db=>db.block_id===documentBlock.block_id?{...db,content:documentBlock.content}:db));
         
         Toast.success('Bloque actualizado correctamente.');

         setModal(null);
      }).catch(()=>{
         return setModalPrompts(['Ocurrio un error al actualizar el bloque. (DCB-UPD-1)']);
      }).finally(()=>{
         setLoading(false);
      });
   };

   var onDeleteDocumentBlock=(block_id: number)=>{
      var bToDel = documentBlocks.find(db=>db.block_id===block_id);
      if(bToDel) {
         setDocumentBlock(bToDel);
         setModal(MODALS.DELETE_ACTION);
      }
   };

   var onConfirmDeleteDocumentBlock=(setLoading: (loading: boolean) => void)=>{
      if(!documentBlock || !documentBlock.block_id) return;
      setLoading(true);
      deleteDocumentBlock(documentBlock.block_id).then(res=>{
         if(res.error) return Toast.error(res.message);
         
         setDocumentBlocks(prev=>prev.filter(db=>db.block_id!==documentBlock.block_id));
         Toast.success('Bloque eliminado correctamente.');
         setModal(null);
      }).catch(()=>{
         Toast.error('Ocurrio un error al eliminar el bloque. (DCB-DEL-1)');
      }).finally(()=>{
         setLoading(false);
      });
   };

   var get=(document_id: number)=>{
      getBlockTypes().then(res=>{
         if(res.error)return setError(res.message);

         setBlockTypes(res.data);
      }).catch(()=>{
         return setError('Ocurrio un error al consultar la informacion. (BT-G-1)');
      });

      getDocumentBlocks(document_id).then(res=>{
         if(res.error)return setError(res.message);

         setDocumentBlocks(res.data);
      }).catch(()=>{
         return setError('Ocurrio un error al consultar la informacion. (DCB-G-1)')
      })
   };

   var onSortEnd = (oldIndex: number, newIndex: number) => {
      setDocumentBlocks((arr)=>{
         var newBlocks=arrayMoveImmutable(arr, oldIndex, newIndex);
         newBlocks=newBlocks.map((db, idx)=>({...db, priority: idx + 1}));
         
         var reorderPayload=newBlocks.map(db=>({block_id: db.block_id, priority: db.priority}));
         reorderDocumentBlocks(reorderPayload).then(res=>{
            if(res.error) Toast.error(res.message || 'Error al guardar el nuevo orden.');
         }).catch(()=>{
            Toast.error('Error al guardar el nuevo orden. (DCB-RDR-1)');
         });
         
         return newBlocks;
      });
   };

   var moveBlock=(index: number, direction: number)=>{
      var _documentBlocks=[...documentBlocks];
      var targetIndex=index+direction;
      if(targetIndex<0||targetIndex>=_documentBlocks.length)return;

      var temp=_documentBlocks[index];
      _documentBlocks[index]=_documentBlocks[targetIndex];
      _documentBlocks[targetIndex]=temp;

      var newBlocks=_documentBlocks.map((db, idx)=>({...db, priority: idx + 1}));
      setDocumentBlocks(newBlocks);

      var reorderPayload=newBlocks.map(db=>({block_id: db.block_id, priority: db.priority}));
      reorderDocumentBlocks(reorderPayload).then(res=>{
         if(res.error) Toast.error(res.message || 'Error al guardar el nuevo orden.');
      }).catch(()=>{
         Toast.error('Error al guardar el nuevo orden. (DCB-RDR-2)');
      });
   };

   useEffect(()=>{
      if(!document_id)return;
      setDocumentBlocks(null);
      get(document_id);
   },[document_id]);

   useEffect(()=>{
      if (!containerRef.current)return;
      
      var observer=new ResizeObserver((entries)=>{
         if(entries[0])setWidth(entries[0].contentRect.width);
      });
      
      observer.observe(containerRef.current);
      return()=>observer.disconnect();
   },[document_id]);
   
   if(error)return <Header text={error} />;
   if(!document_id)return(
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100%' }}>
         <Header text={'Selecciona un documento'} subtext={'Haz clic en un documento del menú lateral para comenzar a editar su contenido'} />
      </div>
   );

   return <div style={{ display: 'flex', padding: 20, gap: 20, height: '100vh', boxSizing: 'border-box', overflow: 'hidden'}}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
         <Toolbar 
            items={[
               { text: "Editor", iconName: "edit", active: viewMode===0, onClick: ()=>setViewMode(0) },
               { text: "Vista previa", iconName: "eye", active: viewMode===1, onClick: ()=>setViewMode(1) }
            ]}
         />
         <div ref={containerRef} style={{flex: 1, overflowY: 'auto', paddingRight: 10 }}>
            {viewMode === 0 ? (
               <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {!documentBlocks?(
                     <Header loading />
                  ):documentBlocks.length<1?(
                     <Header subtext={'Sin contenido'} />
                  ) : (
                     <SortableList onSortEnd={onSortEnd} className="list" draggedItemClassName="dragged">
                        {documentBlocks.map((db, idx) => (
                           <SortableItem key={db.block_id}>
                              <div style={{ userSelect: 'none', marginBottom: 10 }}>
                                 <Groupper title={db.content.title && db.content.title} width={width} actions={
                                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                       <div className="drag-handle" style={{ cursor: 'grab' }}>
                                          <Button 
                                             iconName="arrow-up" 
                                             size="tiny"
                                             color="black"
                                             disabled={idx===0} 
                                             onClick={()=>moveBlock(idx, -1)} 
                                             style={{marginRight: 5}}
                                          />
                                          <Button 
                                             iconName="arrow-down" 
                                             size="tiny"
                                             color="black"
                                             disabled={idx===documentBlocks.length-1} 
                                             onClick={()=>moveBlock(idx, 1)} 
                                             style={{marginRight: 10}}
                                          />
                                       </div>
                                    <div>
                                       <Button 
                                          text="Editar" 
                                          color="black" 
                                          style={{marginRight: 10}} 
                                          onClick={() => {
                                             var type=blockTypes.find(bt => bt.block_type_id === db.block_type);
                                             onOpenModal(type, db);
                                          }} 
                                       />
                                       <Button 
                                          text="Eliminar" 
                                          color="red"
                                          onClick={() => onDeleteDocumentBlock(db.block_id)} 
                                       />
                                    </div>
                                 </div>
                              } >
                                 <div data-color-mode="light">
                                    {db.content.items && db.content.items.length > 0 ? (
                                       db.content.items.map((item, idx) => (
                                          <div key={idx} style={{ marginBottom: 8, padding: 8, border: '1px solid #eaeaea', borderRadius: 6 }}>
                                             <MDEditor.Markdown source={`**${item.title}**`} style={{ whiteSpace: 'pre-wrap', marginBottom: 4 }} />
                                             <MDEditor.Markdown source={item.body} style={{ whiteSpace: 'pre-wrap' }} />
                                          </div>
                                       ))
                                    ) : db.content.bodies && db.content.bodies.length>0?(
                                       db.content.bodies.map((stepBody, idx)=>(
                                          <div key={idx} style={{marginBottom: 8}}>
                                             <MDEditor.Markdown source={`**Paso ${idx + 1}.** ${stepBody}`} style={{ whiteSpace: 'pre-wrap' }} />
                                          </div>
                                       ))
                                    ) : db.content.file?.img ? (
                                       <img src={db.content.file.img} alt={db.content.title || ''} style={{ maxWidth: '100%', borderRadius: 6 }} />
                                    ) : (
                                       <MDEditor.Markdown source={db.content.body} style={{ whiteSpace: 'pre-wrap' }} />
                                    )}
                                 </div>
                              </Groupper>
                              </div>
                           </SortableItem>
                        ))}
                     </SortableList>
                  )}
               </div>
            ) : (
               <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 20, backgroundColor: '#fff', borderRadius: 8, border: '1px solid #eaeaea' }}>
                  {!documentBlocks?(
                     <Header loading />
                  ):documentBlocks.length<1?(
                     <Header subtext={'Sin contenido'} />
                  ):documentBlocks.map((db)=>(
                     <div key={db.block_id} data-color-mode="light">
                        <MDEditor.Markdown source={`# ${db.content.title}`} style={{ whiteSpace: 'normal', marginBottom: 10 }} />
                        {db.content.items && db.content.items.length > 0 ? (
                           db.content.items.map((item, idx) => (
                              <div key={idx} style={{ marginBottom: 8 }}>
                                 <MDEditor.Markdown source={`**${item.title}**`} style={{ whiteSpace: 'normal', marginBottom: 4 }} />
                                 <MDEditor.Markdown source={item.body} style={{ whiteSpace: 'normal' }} />
                              </div>
                           ))
                        ) : db.content.bodies && db.content.bodies.length>0?(
                           db.content.bodies.map((stepBody, idx)=>(
                              <div key={idx} style={{marginBottom: 8}}>
                                 <MDEditor.Markdown source={`**Paso ${idx + 1}.** ${stepBody}`} style={{ whiteSpace: 'normal' }} />
                              </div>
                           ))
                        ) : db.content.file?.img ? (
                           <img src={db.content.file.img} alt={db.content.title || ''} style={{ maxWidth: '100%', borderRadius: 6 }} />
                        ) : (
                           <MDEditor.Markdown source={db.content.body} style={{ whiteSpace: 'normal' }} />
                        )}
                     </div>
                  ))}
               </div>
            )}
         </div>
      </div>
      <div style={{ width: 400 }}>
         <Groupper title={'Componentes'} style={{ gap: 10, display: 'flex', flexDirection: 'column' }}>
            {!blockTypes?(
               <Header loading />
            ):blockTypes.length>0?blockTypes.map((bt)=>(
               <Button 
                  text={bt.name} 
                  iconName="heading" 
                  key={bt.block_type_id}
                  style={{width: '100%'}} 
                  onClick={()=>bt.slug_name===MODALS.DIVIDER?onCreateDirectBlock(bt):onOpenModal(bt)} 
               />
            )):(
               <Header subtext={'Sin componentes'} />
            )}
         </Groupper>
      </div>
      <Modal open={!!modal && modal !== MODALS.DELETE_ACTION} onClose={onCloseModal} size="tiny">
            <Modal.Header>
               {documentBlock.block_id ? 'Editar Bloque:' : 'Agregar Bloque:'} {blockType?.name}
            </Modal.Header>
            <Modal.Content>
               <Input 
                  label={'Titulo del bloque (Opcional)'} 
                  value={documentBlock.content.title}
                  placeholder={'Ej: Descripción general'} 
                  onChange={(val)=>setDocumentBlock({...documentBlock,content:{...documentBlock.content,title: val}})}
               />
               {modal===MODALS.STEPS ? (
                  <>
                     {(documentBlock.content.bodies || ['']).map((stepBody, idx)=>(
                        <div key={idx} style={{ marginBottom: 10 }}>
                           <Field label={`Paso ${idx+1}`}>
                              <div style={{ display: 'flex', gap: 5, alignItems: 'flex-start' }}>
                                 <div data-color-mode="light" style={{ flex: 1 }}>
                                    <MDEditor 
                                       value={stepBody}
                                       onChange={(val)=>{
                                          var newBodies = [...(documentBlock.content.bodies || [''])];
                                          newBodies[idx] = val || '';
                                          setDocumentBlock({
                                             ...documentBlock,
                                             content:{...documentBlock.content, bodies: newBodies}
                                          });
                                       }}
                                       preview="edit"
                                       commands={[
                                          commands.bold,
                                          commands.italic,
                                          commands.codeBlock,
                                       ]}
                                       extraCommands={[]}
                                    />
                                 </div>
                                 {(documentBlock.content.bodies || []).length > 1 && (
                                    <Button 
                                       iconName="trash" 
                                       color="red" 
                                       style={{ marginTop: 2 }}
                                       onClick={()=>{
                                          var newBodies = (documentBlock.content.bodies || []).filter((_, i) => i !== idx);
                                          setDocumentBlock({
                                             ...documentBlock,
                                             content:{...documentBlock.content, bodies: newBodies}
                                          });
                                       }} 
                                    />
                                 )}
                              </div>
                           </Field>
                        </div>
                     ))}
                     <Button 
                        text="Agregar paso" 
                        iconName="plus" 
                        style={{ width: '100%' }}
                        onClick={()=>{
                           var newBodies = [...(documentBlock.content.bodies || []), ''];
                           setDocumentBlock({
                              ...documentBlock,
                              content:{...documentBlock.content, bodies: newBodies}
                           });
                        }}
                     />
                  </>
               ) : modal===MODALS.ACCORDION ? (
                  <>
                     {(documentBlock.content.items || [{title:'', body:''}]).map((item, idx)=>(
                        <div key={idx} style={{ marginBottom: 10, padding: 10, border: '1px solid #eaeaea', borderRadius: 8 }}>
                           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                              <strong>Elemento {idx+1}</strong>
                              {(documentBlock.content.items || []).length > 1 && (
                                 <Button 
                                    iconName="trash" 
                                    color="red" 
                                    size="tiny"
                                    onClick={()=>{
                                       var newItems = (documentBlock.content.items || []).filter((_, i) => i !== idx);
                                       setDocumentBlock({
                                          ...documentBlock,
                                          content:{...documentBlock.content, items: newItems}
                                       });
                                    }} 
                                 />
                              )}
                           </div>
                           <Input 
                              label="Titulo del acordeón"
                              value={item.title}
                              placeholder="Ej: Haz clic para ver más"
                              onChange={(val)=>{
                                 var newItems = [...(documentBlock.content.items || [{title:'', body:''}])];
                                 newItems[idx] = {...newItems[idx], title: val};
                                 setDocumentBlock({
                                    ...documentBlock,
                                    content:{...documentBlock.content, items: newItems}
                                 });
                              }}
                           />
                           <Field label="Contenido">
                              <div data-color-mode="light">
                                 <MDEditor 
                                    value={item.body}
                                    onChange={(val)=>{
                                       var newItems = [...(documentBlock.content.items || [{title:'', body:''}])];
                                       newItems[idx] = {...newItems[idx], body: val || ''};
                                       setDocumentBlock({
                                          ...documentBlock,
                                          content:{...documentBlock.content, items: newItems}
                                       });
                                    }}
                                    preview="edit"
                                    commands={[
                                       commands.bold,
                                       commands.italic,
                                       commands.codeBlock,
                                    ]}
                                    extraCommands={[]}
                                 />
                              </div>
                           </Field>
                        </div>
                     ))}
                     <Button 
                        text="Agregar elemento" 
                        iconName="plus" 
                        style={{ width: '100%' }}
                        onClick={()=>{
                           var newItems = [...(documentBlock.content.items || []), {title: '', body: ''}];
                           setDocumentBlock({
                              ...documentBlock,
                              content:{...documentBlock.content, items: newItems}
                           });
                        }}
                     />
                  </>
               ) : modal===MODALS.IMAGE?(
                  <Field label='Archivo'>
                     <UploadBox
                        type={documentBlock.content.file.img.length>0 ? 'green' : 'normal'}
                        text='Arrastra tu archivo aquí o haz clic para seleccionar'
                        icon='cloud-upload'
                        single
                        maxFileSize={8 * 1024 * 1024}
                        onFile={async(file)=>{
                           var reader=new FileReader();
                           reader.onload=()=>{
                              setDocumentBlock({
                                 ...documentBlock,
                                 content:{...documentBlock.content, file:{type: file.type.split('/')[1], img: reader.result as string}}
                              });
                           };
                           reader.readAsDataURL(file);
                        }}
                     />
                  </Field>
               ):(
                  <Field label="Contenido">
                     <div data-color-mode="light">
                        <MDEditor 
                           value={documentBlock.content.body}
                           onChange={(val)=>setDocumentBlock({
                              ...documentBlock,
                              content:{...documentBlock.content,body:val||""}
                           })}
                           preview="edit"
                           commands={
                              MODALS.IMAGE===modal?[commands.image]:
                              MODALS.CODE===modal?[commands.codeBlock]:
                              MODALS.DIVIDER===modal?[commands.divider]:
                              MODALS.QUOTE===modal?[commands.quote, commands.bold, commands.italic] :
                              MODALS.LIST===modal?[commands.unorderedListCommand, commands.bold, commands.italic]:
                              MODALS.LIST_ENUM===modal?[commands.orderedListCommand, commands.bold, commands.italic]:
                              [
                                 commands.bold,
                                 commands.italic,
                                 commands.strikethrough,
                                 commands.divider,
                                 commands.codeBlock,
                              ]
                           }
                           extraCommands={[]} 
                        />
                     </div>
                  </Field>
               )}
               <Message type='error' list={modalPrompts} />
            </Modal.Content>
            <Modal.Actions>
               <Button text={"Guardar"} color="black" onClick={documentBlock.block_id ? onUpdateDocumentBlock : onCreateDocumentBlock} />
               <Button text="Cancelar" onClick={onCloseModal} />
            </Modal.Actions>
      </Modal>
      <Modal open={modal === MODALS.DELETE_ACTION} size="mini" onClose={()=>setModal(null)}>
         <Modal.Header>Eliminar Bloque</Modal.Header>
         <Modal.Content>
            <Message text="¿Estás seguro de que deseas eliminar este bloque? Esta acción no se puede deshacer." type="warning" />
            {documentBlock.block_id && modal === MODALS.DELETE_ACTION && (
               <Table 
                  striped
                  details 
                  data={[
                     ['ID', documentBlock.block_id],
                     ['Título', documentBlock.content.title]
                  ]} 
               />
            )}
         </Modal.Content>
         <Modal.Actions>
            <Button text="Eliminar" color="red" onClick={onConfirmDeleteDocumentBlock} />
            <Button text="Cancelar" onClick={()=>setModal(null)} />
         </Modal.Actions>
      </Modal>
   </div>
};

export default DocumentEdit;
